import { ACTIVE_CONNECTIONS_KEY, activeConnections, storage } from './storage';
import { ConnectionDescriptor, DomainHandler, HandlerContext, WSRoute } from './types';
import { buildWebSocketURL, buildWebSocketAuthProtocols } from './url';
import { calculateReconnectDelay } from './reconnect';
import { isAuthError, dispatchAuthFailed } from './auth-error';

const COMPONENT_MANAGED_ROUTES: WSRoute[] = ['bom-imports', 'stock-and-po-imports', 'sap-imports'];

interface ConnEntry {
  socket: WebSocket;
  descriptor: ConnectionDescriptor;
  retries: number;
  state: 'CONNECTING' | 'OPEN' | 'CLOSED' | 'RECONNECTING';
  backoffTimer: ReturnType<typeof setTimeout> | null;
}

const MAX_RETRIES = 10;
const TERMINAL_STATUSES = ['FINISHED', 'ERROR', 'CANCELED', 'COMPLETED', 'DONE', 'FAILED'];

class WebSocketOrchestrator {
  private connections = new Map<string, ConnEntry>();
  private handlers = new Map<WSRoute, DomainHandler>();
  private isShuttingDown = false;

  init(): void {
    const all = activeConnections.list();
    const toReconnect: typeof all = [];
    const toPrune: typeof all = [];

    for (const e of all) {
      if (e.status === 'CLOSED') { toPrune.push(e); continue; }
      // Component-managed routes (BOM, Stock-and-PO imports) are opened on-demand by their
      // view — stale entries from crashes or previous sessions must not be auto-reconnected.
      if (COMPONENT_MANAGED_ROUTES.includes(e.route)) { toPrune.push(e); continue; }
      // Entries with no valid id are orphans from buggy previous code — prune them
      if (!e.id) { toPrune.push(e); continue; }
      const data = storage.get<{ status?: string }>(e.storageKey);
      if (data?.status && TERMINAL_STATUSES.includes(data.status)) { toPrune.push(e); continue; }
      toReconnect.push(e);
    }

    if (toPrune.length > 0) {
      for (const e of toPrune) storage.remove(e.storageKey);
      storage.set(ACTIVE_CONNECTIONS_KEY, toReconnect);
      console.log(`[Orchestrator] init: pruned ${toPrune.length} stale entries`);
    }

    console.log(`[Orchestrator] init: reconnecting ${toReconnect.length} connections`);

    for (const entry of toReconnect) {
      this.addConnection({ route: entry.route, id: entry.id, storageKey: entry.storageKey });
    }

    window.addEventListener('auth-logout', () => this.closeAll());
  }

  setup(handlers: Partial<Record<WSRoute, DomainHandler>>): void {
    for (const [route, handler] of Object.entries(handlers) as [WSRoute, DomainHandler][]) {
      this.handlers.set(route, handler);
    }
    this.init();
  }

  registerHandler(route: WSRoute, handler: DomainHandler): void {
    this.handlers.set(route, handler);
  }

  addConnection(descriptor: ConnectionDescriptor): void {
    const { storageKey } = descriptor;

    if (this.connections.has(storageKey)) return;

    activeConnections.upsert({
      route: descriptor.route,
      id: descriptor.id,
      storageKey,
      status: 'CONNECTING',
      registeredAt: Date.now(),
    });

    this.openSocket(descriptor, 0);
  }

  removeConnection(storageKey: string): void {
    const entry = this.connections.get(storageKey);
    if (!entry) return;

    if (entry.backoffTimer) clearTimeout(entry.backoffTimer);
    entry.socket.close(1000);
    this.connections.delete(storageKey);
    activeConnections.remove(storageKey);
  }

  send(storageKey: string, payload: unknown): void {
    const entry = this.connections.get(storageKey);
    if (entry?.socket.readyState === WebSocket.OPEN) {
      entry.socket.send(JSON.stringify(payload));
    }
  }

  trySend(storageKey: string, payload: unknown): boolean {
    const entry = this.connections.get(storageKey);
    if (entry?.socket.readyState === WebSocket.OPEN) {
      entry.socket.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }

  closeAll(): void {
    this.isShuttingDown = true;
    for (const [, entry] of this.connections) {
      if (entry.backoffTimer) clearTimeout(entry.backoffTimer);
      entry.socket.close(1000);
    }
    this.connections.clear();
    activeConnections.clear();
    this.isShuttingDown = false;
  }

  private openSocket(descriptor: ConnectionDescriptor, retries: number): void {
    const { storageKey, route, id } = descriptor;
    const url = buildWebSocketURL(route, id);
    const protocols = buildWebSocketAuthProtocols();
    const socket = protocols ? new WebSocket(url, protocols) : new WebSocket(url);

    const ctx: HandlerContext = {
      descriptor,
      send: (payload) => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify(payload));
        }
      },
      closeSelf: (code = 1000) => {
        socket.close(code);
      },
    };

    const entry: ConnEntry = {
      socket,
      descriptor,
      retries,
      state: 'CONNECTING',
      backoffTimer: null,
    };

    this.connections.set(storageKey, entry);

    socket.onopen = () => {
      if (this.isShuttingDown) return;
      entry.state = 'OPEN';
      entry.retries = 0;
      activeConnections.upsert({ route, id, storageKey, status: 'OPEN', registeredAt: Date.now() });

      const handler = this.handlers.get(route);
      handler?.onOpen?.(ctx);
    };

    socket.onmessage = (event) => {
      if (this.isShuttingDown) return;
      const handler = this.handlers.get(route);
      if (!handler) return;

      let msg: unknown;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }

      const isTerminal = handler.onMessage(msg, ctx);
      if (isTerminal) {
        activeConnections.remove(storageKey);
        this.connections.delete(storageKey);
        socket.close(1000);
      }
    };

    socket.onclose = (event) => {
      if (this.isShuttingDown) return;

      const handler = this.handlers.get(route);
      handler?.onClose?.(event, ctx);

      if (isAuthError(event.code)) {
        dispatchAuthFailed(storageKey, event.code);
        this.connections.delete(storageKey);
        activeConnections.remove(storageKey);
        return;
      }

      // code 1000 = normal closure (terminal message already cleaned up)
      if (event.code === 1000) {
        this.connections.delete(storageKey);
        return;
      }

      const nextRetries = retries + 1;

      if (nextRetries > MAX_RETRIES) {
        console.warn(`[Orchestrator] ${storageKey} exceeded max retries (${MAX_RETRIES}), giving up`);
        this.connections.delete(storageKey);
        activeConnections.remove(storageKey);
        storage.remove(storageKey);
        return;
      }

      const delay = calculateReconnectDelay(nextRetries);
      entry.state = 'RECONNECTING';
      activeConnections.upsert({ route, id, storageKey, status: 'RECONNECTING', registeredAt: Date.now() });

      console.warn(`[Orchestrator] ${storageKey} closed (${event.code}), reconnecting in ${delay}ms (attempt ${nextRetries})`);

      entry.backoffTimer = setTimeout(() => {
        this.connections.delete(storageKey);
        this.openSocket(descriptor, nextRetries);
      }, delay);
    };

    socket.onerror = () => {
      // onclose fires right after; nothing to do here
    };
  }
}

export const orchestrator = new WebSocketOrchestrator();
